<script setup>

import TopBar from "../components/ui-components/TopBar.vue";
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import AppToast from "../components/ui-components/neo-layout/AppToast.vue";
import {usePage} from "@inertiajs/vue3";
import {useToast} from "primevue/usetoast";
import {useConfirm} from "primevue/useconfirm";
import MobileBottomBar from "../components/ui-components/MobileBottomBar.vue";
import {Head} from "@inertiajs/vue3";
import Feedback from "../components/feedback/Feedback.vue";
import LogoLoader from "../../widget/components/ui-components/LogoLoader.vue";
import ConfirmDialog from "primevue/confirmdialog";
import Skeleton from "./NeoLayout/Skeleton.vue";
import AppSidebar from "../components/ui-components/neo-layout/AppSidebar.vue";
import AppTopBar from "../components/ui-components/neo-layout/AppTopBar.vue";
import NeoMain from "./NeoLayout/NeoMain.vue";
import NeoAside from "./NeoLayout/NeoAside.vue";
import AppMobileBar from "../components/ui-components/neo-layout/AppMobileBar.vue";

const page = usePage();
const toast = useToast();
const confirm = useConfirm();

// Hard stop (variant: 'error') dostane vycentrovaný dialog místo toastu — pro věci
// jako "přesun se zamítl kvůli kolizi" chceme, aby si toho uživatel nemohl nevšimnout.
watch(() => page.props?.notification, (now) => {
    if (!now?.message) return

    if (now.variant === 'error') {
        confirm.require({
            group: 'hard-stop-dialog',
            header: now.header || 'Nelze provést',
            message: now.message,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'OK',
            rejectProps: { style: { display: 'none' } },
            accept: () => {},
        })
        return
    }

    toast.add({
        severity: now.variant,
        summary: now.header || 'Roori',
        detail: now.message,
        life: 2000,
    })
})

const justLoggedIn = computed(() => page.props?.justLoggedIn);
const loaded = ref(false);


const windowWidth = ref(window.innerWidth);

const updateWidth = () => {
    windowWidth.value = window.innerWidth;
};

onMounted(() => {
    window.addEventListener('resize', updateWidth);
    loaded.value = true;
});

onUnmounted(() => {
    window.removeEventListener('resize', updateWidth);
});

const settingsActive = ref(false)

const prevComponent = ref(null)
watch(() => page.component, (newVal, oldVal) => { prevComponent.value = oldVal })
const transitionName = computed(() => {
    const isReservations = (c) => c?.includes('ReservationsView')
    return isReservations(page.component) || isReservations(prevComponent.value) ? 'fade' : 'list-page'
});

const useNewLayout = ref(true)

const isThin = computed(() => {
    return page.props.__neo_main?.__flags ? Object.values(page.props.__neo_main?.__flags)?.includes('thin') : false
})

const isSettings = computed(() => {
    return page.props.__neo_main?.__flags ? Object.values(page.props.__neo_main?.__flags)?.includes('settings') : false
})

const isFlush = computed(() => {
    return page.props.__neo_main?.__flags ? Object.values(page.props.__neo_main?.__flags)?.includes('flush') : false
})

// Na Skeletonu (přetrvává napříč navigacemi), ne na NeoMain (to se při každé navigaci
// remountuje) — díky tomu je pozadí správně hned jak Inertia vymění props, ne až se
// dohraje fade animace nové stránky.
const isFilled = computed(() => {
    return page.props.__neo_main?.__flags ? Object.values(page.props.__neo_main?.__flags)?.includes('filled') : false
})

const isNarrowAside = computed(() => {
    return page.props.__neo_main?.__flags ? Object.values(page.props.__neo_main?.__flags)?.includes('narrow_aside') : false
})

const isSemiNarrowAside = computed(() => {
    return page.props.__neo_main?.__flags ? Object.values(page.props.__neo_main?.__flags)?.includes('seminarrow_aside') : false
})

const isModalAside = computed(() => {
    return page.props.__neo_main?.__flags ? Object.values(page.props.__neo_main?.__flags)?.includes('modal_aside') : false
})

// Modal aside je jen pro desktop (stejný breakpoint jako Skeleton.vue isMobile) —
// na mobilu se aside vždy chová jako dřív (bottom-sheet s šipkou zpět).
const isDesktopModalAside = computed(() => isModalAside.value && windowWidth.value >= 900)
</script>

<template>
    <ConfirmDialog></ConfirmDialog>
    <ConfirmDialog group="hard-stop-dialog"></ConfirmDialog>
    <logo-loader v-if="justLoggedIn" main></logo-loader>
    <div class="app relative h-dvh overflow-x-hidden">
        <div id="progress-bar"></div>
        <Head :title="$page.props.title"></Head>
        <AppToast/>
        <Skeleton v-if="useNewLayout"
                  :show-aside="!!$page.props.__neo_aside"
                  :thin="isThin" :settings="isSettings" :filled="isFilled"
                  :narrow-aside="isNarrowAside"
                  :semi-narrow-aside="isSemiNarrowAside"
                  :modal-aside="isModalAside"
                  :version="$page.props.__neo_main?.__feature?.version"
                  :feature="$page.props.__neo_main?.__feature?.name"
        >
            <template #sidebar>
                <AppSidebar></AppSidebar>
            </template>
            <template #topbar>
                <AppTopBar :is-mobile="windowWidth <= 768" :has-bg="isFlush"></AppTopBar>
            </template>
            <template #content>
                <transition :name="transitionName" mode="out-in">
                    <NeoMain :key="$page.component" v-if="$page.props.__neo_main" :obj="$page.props.__neo_main"></NeoMain>
                    <div v-else :key="$page.component+'a'" class="relative overflow-hidden w-full h-dvh">
                        <div class="mx-6 overflow-x-hidden flex-col gap-4">
                            <slot/>
                        </div>
                    </div>
                </transition>
            </template>
            <template #aside>
                <NeoAside v-if="$page.props.__neo_aside"
                          :key="$page.props.__neo_aside?.__title"
                          :is-settings="isSettings"
                          :is-modal="isDesktopModalAside"
                          :obj="$page.props.__neo_aside"
                          :close-route="$page.props.__neo_main.__route"
                          :flags="page.props.__neo_main?.__flags"
                />
            </template>
            <template #bottombar>
                <AppMobileBar></AppMobileBar>
            </template>
        </Skeleton>
        <template v-else>
            <transition name="list" mode="out-in">
                <div v-show="$page.component === 'administration/views/AdministrationMainView'"
                     class="absolute transition-all md:bg-roori-home bg-top bg-contain bg-no-repeat h-full top-0 -mt-4 z-0 w-full pointer-events-none"></div>
            </transition>
            <TopBar :is-mobile="windowWidth <= 768" @open-settings="() => settingsActive = true">
                <slot name="top-bar">
                    <div id="top-bar-teleport" style="height: 36px"></div>
                </slot>
            </TopBar>

            <transition name="list-page" mode="out-in">
                <div :key="$page.component" class="flex justify-center w-full">
                    <div class="rr-container mx-6 overflow-x-hidden flex-col gap-4">
                        <slot/>
                    </div>
                </div>
            </transition>
            <MobileBottomBar v-if="windowWidth <= 768" @open-settings="() => settingsActive = true"
                             :visible="$page.component !== 'administration/wizards/registrationForm/NewRegistrationFormWizard'"></MobileBottomBar>
        </template>

    </div>
</template>

<style scoped lang="scss">

</style>
